// @react
import React from 'react'
// @types
import {
  GetRecommendedContentQuery,
  QueryRecommendedContentArgs,
  RecommendedContentItem,
  GetUserQuery,
  GetUserQueryVariables,
} from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_RECOMMENDED_CONTENT } from 'graphql/queries/recommendedContent.query'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import clsx from 'clsx'
// @common
import { makeStyles } from '@material-ui/core/styles'
import { Typography, useMediaQuery } from '@material-ui/core'
import strings from 'lib/constants/strings'
import Helper from 'utils/Helper.class'
// @components
import Grid from '@bit/atd.web.grid'
import Accordion from '@bit/atd.web.accordion'
import Icon from '@bit/atd.web.icons'
import colors from '@bit/atd.web.colors'
import Spacer from '@bit/atd.web.spacer'
import Box from '@bit/atd.web.box'
import Button from '@bit/atd.web.button'
import Carousel from '__COMPONENTS_TO_MIGRATE/Carousel'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import ContentCard from 'modules/common/components/ContentCard'
import QueryError from 'modules/common/components/Errors/QueryError'

/**
 * 2.0.0.7 MyATD - Dashboard - Recommended Content
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3579%3A14122
 *
 * endpoint: user/profile
 * response: UserProfileDTO
 * response namespaces: User/Profile, User/Job
 *
 * endpoint: algolia/relatedContentByTopic
 * response: GeneralContentDTO
 * response namespaces:
 */

const useStyles = makeStyles({
  accordionBox: {
    paddingBottom: 0,
    '& > div': {
      padding: '0 !important', // @todo fix this component level
    },
  },
  accordionRoot: {
    padding: 0,
    '& > div:first-child': {
      borderTop: 'none',
    },
    '& > div': {
      borderWidth: '1px',
    },
    '& .MuiAccordion-root > .MuiButtonBase-root': {
      minHeight: '64px',
      paddingLeft: '24px',
      paddingRight: '24px',
    },
    '& .MuiAccordionDetails-root': {
      borderTop: '1px solid #dbd9d6',
      paddingTop: '24px',
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingBottom: '24px',
    },
    '& .MuiAccordionSummary-content > h2': {
      fontSize: '18px',
      lineHeight: '24px',
    },
    '& .MuiAccordionSummary-content > .MuiSvgIcon-root': {
      height: '24px',
      width: '24px',
    },
  },
  isCarousel: {
    '& .MuiAccordionDetails-root': {
      paddingRight: '0',
    },
  },
})

type PropsType = any

const RecommendedContent = (props: PropsType) => {
  /******************************************
   * PROPS
   ******************************************/

  const pageSize = 6

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()
  const showCarousel = useMediaQuery('(max-width: 580px)')

  /******************************************
   * GQL HOOKS
   ******************************************/

  const { loading: isUserLoading, data: userData } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER)

  const {
    loading: isContentLoading,
    error: isContentError,
    data: contentData,
  } = useQuery<GetRecommendedContentQuery, QueryRecommendedContentArgs>(
    GET_RECOMMENDED_CONTENT,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        input: { pageSize },
      },
    },
  )

  /******************************************
   * RENDER
   ******************************************/

  const isLoading = isUserLoading || isContentLoading

  if (isContentError || (!contentData && !isLoading)) {
    return <QueryError />
  }

  const topicButton = (action: string, isDisabled?: boolean) => (
    <Button
      disabled={isDisabled}
      size="small"
      variant="secondary"
      href={Helper.getPathFromRouteKey('PROFILE_TOPICS')}
    >
      <Icon glyph="edit" size={16} color={colors.ctaPrimary} />
      <Spacer size={8} />
      <span>{action}</span>
    </Button>
  )

  if (
    !contentData ||
    !userData?.user?.topics.filter((item) => item.userStatus.followed)?.length
  ) {
    return (
      <Box
        variant={'subModule'}
        header={{
          title: strings.DASHBOARD.RECOMMENDED.TITLE,
        }}
      >
        {/*// @TODO fix duplicate border issue since variant transparent has no padding*/}
        <Grid container>
          <Grid item xs={12}>
            <Typography component={'h3'}>
              {strings.DASHBOARD.RECOMMENDED.EMPTY_STATE_TITLE}
            </Typography>
          </Grid>
          <p>{strings.DASHBOARD.RECOMMENDED.EMPTY_STATE_DESCRIPTION}</p>
          {topicButton(strings.DASHBOARD.RECOMMENDED.ADD)}
        </Grid>
      </Box>
    )
  }

  const renderContentGrid = (contentItem: RecommendedContentItem) => (
    <Grid container spacing={2} justify="center">
      {/* Cards */}
      {contentItem.data?.map((item: any, index: number) => {
        return (
          <Grid
            key={`${item.__typename}_${item.id}`}
            item
            xs={index < 3 ? 4 : 12}
          >
            <ContentCard
              typename={item.__typename}
              type={'dashboard'}
              item={item}
            />
          </Grid>
        )
      })}

      {/* See All Button */}
      {contentItem?.count && contentItem.count > pageSize && (
        <Grid item xs={12}>
          <Button
            variant="primary"
            href={`${Helper.constructSearchURL({
              refinementList: [
                {
                  refinement: 'topics',
                  value: contentItem.topic || '',
                },
              ],
            })}`}
          >
            <span>See all</span>
          </Button>
        </Grid>
      )}
    </Grid>
  )

  const renderContentCarousel = (contentItem: RecommendedContentItem) => {
    return contentItem.data ? (
      <Carousel
        showDots
        sameHeight
        showBtn={
          <Button
            variant="primary"
            href={`${Helper.constructSearchURL({
              refinementList: [
                {
                  refinement: 'topics',
                  value: contentItem.topic || '',
                },
              ],
            })}`}
          >
            <span>See all</span>
          </Button>
        }
        advancedItems={1}
      >
        {contentItem.data?.map((item: any) => {
          return (
            <ContentCard
              key={`${item.__typename}_${item.id}`}
              typename={item.__typename}
              type={'dashboard'}
              item={item}
            />
          )
        })}
      </Carousel>
    ) : null
  }

  return (
    <Box
      variant={'transparent'}
      header={{
        title: 'Recommended for you',
        actions: topicButton('Edit Topics', isLoading),
        actionsAlignment: 'right',
      }}
    >
      <Box
        classNames={{
          root: classes.accordionBox,
        }}
        variant={'simple'}
      >
        {!isLoading ? (
          <Accordion
            className={clsx(classes.accordionRoot, {
              [classes.isCarousel]: showCarousel,
            })}
            defaultPanel={0}
            iconSize={32}
            panels={contentData.recommendedContent.data.map((item) => {
              return {
                title: <h2>{item.topic}</h2>,
                content: showCarousel
                  ? renderContentCarousel(item)
                  : renderContentGrid(item),
              }
            })}
          />
        ) : (
          <SkeletonLoader />
        )}
      </Box>
    </Box>
  )
}

export default RecommendedContent
